import { create } from "zustand";
import { persist } from "zustand/middleware";
import { authApi } from "@/features/auth/api";
import type { Account } from "@/features/auth/types";

interface AuthState {
  account: Account | null;
  basicAuthHeader: string | null;
  hasHydrated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** Applies a new account (e.g. changed email) while keeping the current password in the Basic Auth header. */
  setAccountAndEmail: (account: Account) => void;
  /** Rebuilds the Basic Auth header after a password change, so the next request doesn't 401. */
  setPassword: (password: string) => void;
  setHasHydrated: (value: boolean) => void;
}

/**
 * The server has no token endpoint (see SecurityConfig): API-key management is guarded by
 * stateless HTTP Basic, so every authenticated request must resend the credentials. We keep
 * them base64-encoded, matching the `Authorization: Basic ...` header shape.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      account: null,
      basicAuthHeader: null,
      hasHydrated: false,

      login: async (email, password) => {
        const account = await authApi.login(email, password);
        set({ account, basicAuthHeader: btoa(`${email}:${password}`) });
      },

      register: async (email, password) => {
        const account = await authApi.register(email, password);
        set({ account, basicAuthHeader: btoa(`${email}:${password}`) });
      },

      logout: () => set({ account: null, basicAuthHeader: null }),

      setAccountAndEmail: (account) => {
        const { basicAuthHeader } = get();
        if (!basicAuthHeader) return;
        const password = atob(basicAuthHeader).slice(atob(basicAuthHeader).indexOf(":") + 1);
        set({ account, basicAuthHeader: btoa(`${account.email}:${password}`) });
      },

      setPassword: (password) => {
        const { account } = get();
        if (!account) return;
        set({ basicAuthHeader: btoa(`${account.email}:${password}`) });
      },

      setHasHydrated: (value) => set({ hasHydrated: value }),
    }),
    {
      name: "ogma-license-auth",
      // localStorage (not sessionStorage) so signing in in one tab keeps every other tab —
      // and new tabs — signed in too, instead of forcing a re-login per tab.
      storage: {
        getItem: (key) => {
          const value = localStorage.getItem(key);
          return value ? JSON.parse(value) : null;
        },
        setItem: (key, value) => localStorage.setItem(key, JSON.stringify(value)),
        removeItem: (key) => localStorage.removeItem(key),
      },
      // Storage is synchronous, so zustand finishes hydration synchronously inside `create()`,
      // before the `useAuthStore` binding below is assigned — referencing it here would throw
      // a temporal-dead-zone error (silently swallowed by zustand, leaving hasHydrated stuck at
      // false). Use the state handed to the callback instead, with the pre-hydration snapshot as
      // a fallback for the error path (where zustand calls back with `state: undefined`).
      onRehydrateStorage: (initialState) => (state, error) => {
        (state ?? initialState).setHasHydrated(true);
        if (error) {
          console.error("Failed to rehydrate auth state", error);
        }
      },
    },
  ),
);
